import { useCallback, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Coordinate } from "ol/coordinate";
import RouteLineVectorTileLayer from "./layers/RouteLineVectorTileLayer";
import SelectedRouteLineLayer from "./layers/SelectedRouteLineLayer";
import RouteLineMapClick from "./interactions/RouteLineMapClick";
import RouteLineSummaryOverlay from "./overlays/RouteLineSummaryOverlay";
import RouteLineMessages from "./messages/RouteLineMessages";
import { RouteLineViewTools } from "./controls";
import { MapView } from "../../../../constant/VectorTileLayerType";
import type { RouteLineSummary } from "./RouteLineSummary";
import { useViewContext } from "../../../../context/View/ViewContext";
import { useRouteLineContext } from "../../../../context/MapViews/RouteLine/RouteLineContext";
import SearchBarContainer from "../../tools/SearchbarContainer";
import { SearchBar } from "../../../subheader/SearchBar";
import { BoundarySelection } from "../../../../types/Boundary/BoundarySelection";
import { Layers } from "../../../reactOL/layers";

const SEARCH_BAR_OFFSET_PX = 80;

const RouteLineMapView = () => {
  const visible = useParams().layer === MapView.ROUTE_LINE;
  const location = useLocation();
  const [summaries, setSummaries] = useState<RouteLineSummary[] | null>(null);
  const [coordinate, setCoordinate] = useState<Coordinate | null>(null);
  const { view } = useViewContext();

  const { routeLines, selectedRouteLine, showOverlay, setShowOverlay } = useRouteLineContext();

  const handleSelect = useCallback(
    (summaries: RouteLineSummary[], coordinate: Coordinate) => {
      setSummaries(summaries);
      setCoordinate(coordinate);
      setShowOverlay(true);
      view.animate({ center: coordinate });
    },
    [view, setShowOverlay],
  );

  const handleClose = useCallback(() => {
    setShowOverlay(false);
    setSummaries(null);
    setCoordinate(null);
  }, [setShowOverlay]);

  const changeBoundaries = useCallback(
    (boundary: BoundarySelection) => {
      view.fit(boundary.geometry?.getExtent(), {
        padding: [SEARCH_BAR_OFFSET_PX, 100, 100, 100],
        duration: 1000,
      });
    },
    [view],
  );

  return (
    <>
      <SearchBarContainer>
        {location.pathname.includes(MapView.ROUTE_LINE) && (
          <SearchBar onChange={changeBoundaries} />
        )}
      </SearchBarContainer>

      <Layers>
        <RouteLineVectorTileLayer visible={visible} />
        <SelectedRouteLineLayer selectedRouteLine={selectedRouteLine} />
      </Layers>

      <RouteLineMapClick active={visible} onSelect={handleSelect} />
      <RouteLineSummaryOverlay
        summaries={showOverlay ? summaries : null}
        coordinate={showOverlay ? coordinate : null}
        onClose={handleClose}
      />
      {visible && (
        <>
          <RouteLineViewTools />
          <RouteLineMessages />
        </>
      )}
    </>
  );
};

export default RouteLineMapView;
